import { NoTransactionsMessage, ViewAllButton } from '@components'
import NoTransactionsImage from '@images/icons/transactions-icon.png'
import cn from 'classnames'
import { paths, useGetTransactionsQuery } from 'mmfintech-backend-api'
import type { TransactionHistoryFilterRequest } from 'mmfintech-backend-api/types'
import { isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'
import { useEffect, useState } from 'react'
import LatestTransaction from './LatestTransaction'

import './LatestTransactions.scss'

interface ILatestTransactionsProps {
  filter?: TransactionHistoryFilterRequest & { prepaidCardIds?: number[] }
  title?: string
  showDepositMessage?: boolean
  merchantId?: number
  transactionLoading?: boolean
  isCardView?: boolean
  compact?: boolean
}

export const LatestTransactions = ({
  filter,
  title,
  showDepositMessage = true,
  transactionLoading,
  compact = false
}: ILatestTransactionsProps) => {
  const [showButton, setShowButton] = useState<boolean>(false)
  const { accountId, counterpartyPaymentInstrumentId } = filter || {}

  const {
    data: transactionsResponse,
    error: transactionsError,
    isFetching: transactionsFetching
  } = useGetTransactionsQuery({
    size: 3,
    sort: 'reqTime-desc,id-desc',
    ...filter
  })

  useEffect(() => {
    if (isValidArray(transactionsResponse?.content)) {
      setShowButton(true)
    } else {
      setShowButton(false)
    }
  }, [transactionsResponse])

  return (
    <div className={cn('transaction-section-wrapper', { compact })}>
      <div className='transaction-section-header'>
        <h3 data-test='transaction-section-title'>
          {title || tr('FRONTEND.DASHBOARD.LATEST_TRANSACTIONS_TITLE', 'Latest transactions')}
        </h3>
        {showButton && !transactionLoading && (
          <ViewAllButton
            label={tr('FRONTEND.DASHBOARD.TRANSACTIONS.BUTTON.VIEW_ALL', 'View all')}
            redirect={paths.banking.transactions.list()}
            accountId={accountId}
            recipientId={counterpartyPaymentInstrumentId}
          />
        )}
      </div>
      {transactionsFetching || transactionLoading ? (
        <Spinner />
      ) : transactionsError ? (
        <ErrorDisplay error={transactionsError} />
      ) : isValidArray(transactionsResponse?.content) ? (
        <div className='transaction-section-content'>
          {transactionsResponse.content.map(data => {
            const {
              id,
              reqTime,
              amount,
              currency,
              direction,
              status,
              trnTypeLocalizationKey,
              transactionCategory,
              cardPaymentDetails
            } = data

            return (
              <LatestTransaction
                data-test={`latest-transaction-${id}`}
                key={id}
                amount={amount}
                currency={currency}
                direction={direction}
                reqTime={reqTime}
                status={status}
                trnTypeLocalizationKey={trnTypeLocalizationKey}
                transactionCategory={transactionCategory}
                cardPaymentDetails={cardPaymentDetails}
              />
            )
          })}
        </div>
      ) : (
        <NoTransactionsMessage
          iconLink={NoTransactionsImage}
          showDepositMessage={showDepositMessage}
          accountId={accountId}
        />
      )}
    </div>
  )
}
